'use client';

import type { Yarn } from '@/lib/types';
import YarnTile from './YarnTile';
import { Chip } from './ui/Chip';
import { DashedAddButton } from './ui/DashedAddButton';
import YarnSwatch from './YarnSwatch';
import { formatSkeinCount } from '@/lib/skeinFormat';

/**
 * YarnRowCard — the rich card used in the BY ALPHABET (default) view of My Yarns.
 *
 * This is the "big left tile" presentation:
 * - Large 88px YarnTile on the left (1–4 colorways in a smart grid)
 * - Full stats on the right (with per-colorway over-allocation support via `perYarnOver`)
 * - "Add colorway" affordance on multi-colorway cards
 *
 * Extracted during P0 structural work (build 1422) to keep `yarn/page.tsx` focused
 * on data + orchestration rather than presentation.
 *
 * ─────────────────────────────────────────────────────────────────────
 * VISUAL TWIN: src/components/YarnGroupCard.tsx (used in the BY COLOR /
 * BY BRAND / BY WEIGHT grouped views).
 *
 * The two cards render different domain objects — this one shows a
 * single yarn (or yarn-name colorway group); the twin shows an
 * aggregate bucket of yarns — but they SHARE the same visual recipe:
 * YarnTile + bold uppercase title + mono subline + big tabular skein
 * number + AVAILABLE caption + bottom swatch row. Coherence is
 * maintained by both files using the same primitives (`YarnTile`,
 * `Chip`, `YarnSwatch`, `formatSkeinCount`, the `--paper-2` / `--ink`
 * tokens) — NOT by inheritance or composition.
 *
 * If you change layout, padding, font, or token usage here, change
 * YarnGroupCard.tsx the same way in the same commit — or explicitly
 * document the intentional divergence in both files. Build 1436 had
 * to restore this card's twin after a silent simplification at
 * commit 058b2db caused the two to drift; the docblock pairing exists
 * to make that drift visible to the next editor before it ships.
 *
 * Why not one unified component? Two domain compositions sharing a
 * visual recipe via shared primitives is the correct pattern at this
 * scale — investigated and chosen 2026-05-18 (task #16). A slot-based
 * unifier would relocate domain logic without simplifying it.
 * ─────────────────────────────────────────────────────────────────────
 */

export type YarnRowCardLabels = {
  available: string;
  inProgress: string;
  addColorway: string;
};

interface YarnRowCardProps {
  yarns: Yarn[];
  totalAvailable: number;
  totalInUse: number;
  totalPartialGrams: number;
  anyOver: boolean;
  perYarnOver?: boolean[];
  labels: YarnRowCardLabels;
  onCardClick: () => void;
  onColorwayClick?: (yarn: Yarn) => void;
  onAddColorway?: () => void;
}

// Local display font – only used by this richer alphabet card.
const fontYarnDisplay = "var(--font-geist-sans), system-ui, -apple-system, sans-serif";

export function YarnRowCard({
  yarns,
  totalAvailable,
  totalInUse,
  totalPartialGrams,
  anyOver,
  perYarnOver,
  labels,
  onCardClick,
  onColorwayClick,
  onAddColorway,
}: YarnRowCardProps) {
  const multi = yarns.length > 1;
  const head = yarns[0];
  const borderColor = anyOver ? 'var(--over)' : 'var(--ink)';
  const numberColor = anyOver ? 'var(--over-num)' : 'var(--ink)';
  const cleanedWeight =
    head.weight && head.weight.toLowerCase() !== "i don't know" ? head.weight : '';

  return (
    <div
      data-testid="yarn-row-card"
      data-yarn-name={head.name}
      data-over={anyOver ? 'true' : 'false'}
      style={{
        width: '100%',
        background: 'var(--paper-2)',
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 88,
      }}
    >
      {/* role=button (not <button>) so the colourway-swatch <button>s
           inside the body can nest without an HTML-spec violation. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick();
          }
        }}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'stretch',
          flex: 1,
          color: 'inherit',
        }}
      >
        <YarnTile yarns={yarns} borderColor={borderColor} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 12px 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: fontYarnDisplay,
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  lineHeight: 0.95,
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {head.name}
              </div>
              {head.brand && (
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9,
                    letterSpacing: '0.12em',
                    color: 'var(--ink-2)',
                    marginTop: 3,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {head.brand}
                </div>
              )}
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 4,
                  alignItems: 'flex-start',
                }}
              >
                {head.fiber && <Chip>{head.fiber}</Chip>}
                {cleanedWeight && <Chip>{cleanedWeight}</Chip>}
                {!multi && head.colorName && (
                  <Chip>
                    {head.colorName}
                    {head.colorNumber ? ` #${head.colorNumber}` : ''}
                  </Chip>
                )}
              </div>
            </div>
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                <span
                  data-testid="yarn-card-available"
                  style={{
                    fontFamily: fontYarnDisplay,
                    fontWeight: 800,
                    fontSize: 18,
                    letterSpacing: '-0.02em',
                    color: numberColor,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {formatSkeinCount(totalAvailable, yarns[0]?.gramsPerSkein)}
                </span>
                {totalPartialGrams > 0 ? (
                  <span
                    data-testid="yarn-card-uncounted-grams"
                    style={{
                      fontFamily: fontYarnDisplay,
                      fontWeight: 800,
                      fontSize: 13,
                      color: numberColor,
                      lineHeight: 1,
                    }}
                  >
                    +{totalPartialGrams}g
                  </span>
                ) : null}
              </div>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 6.5,
                  letterSpacing: '0.18em',
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  marginTop: 2,
                }}
              >
                {labels.available}
              </span>
              {totalInUse > 0 && (
                <span
                  data-testid="yarn-card-in-use"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 6.5,
                    letterSpacing: '0.18em',
                    color: anyOver ? 'var(--over-num)' : 'var(--in-use)',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    marginTop: 4,
                    lineHeight: 1,
                  }}
                >
                  {formatSkeinCount(totalInUse, yarns[0]?.gramsPerSkein)} {labels.inProgress}
                </span>
              )}
            </div>
          </div>
          {multi && (
            <div
              className="flex flex-wrap gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              {yarns.map((c, i) => {
                const isOver = perYarnOver?.[i] ?? false;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onColorwayClick?.(c);
                    }}
                    aria-label={`${c.colorName || 'colorway'}${c.colorNumber ? ` #${c.colorNumber}` : ''}`}
                    title={`${c.colorName || ''}${c.colorNumber ? ` #${c.colorNumber}` : ''}`}
                    className={`w-6 h-6 rounded-md border transition-transform hover:scale-110 overflow-hidden ${
                      isOver ? 'border-red-400 ring-1 ring-red-300' : 'border-border-light'
                    }`}
                  >
                    <YarnSwatch yarn={c} className="w-full h-full" lazy />
                  </button>
                );
              })}
              <DashedAddButton
                size="round"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddColorway?.();
                }}
                aria-label={labels.addColorway}
                style={{
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-accent)',
                  background: 'var(--paper-2)',
                }}
              >
                +
              </DashedAddButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default YarnRowCard;